<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <!-- En-tête avec barre de recherche -->
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-flex align-items-center">
        <form class="search-box position-relative me-15" @submit.prevent="rechercher">
          <input
            type="text"
            v-model="searchTerm"
            @keyup="rechercher"
            class="form-control shadow-none text-black rounded-0 border-0"
            placeholder="Rechercher un avis..."
          />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <!-- Corps de la carte -->
    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Auteur</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Mail auteur</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Talent commenté</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Commentaire</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Type d'avis</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0">Date</th>
              <th class="text-uppercase fw-medium text-body-tertiary fs-13 pt-0 pe-0">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="avis in aviss" :key="avis.id">
              <td>{{ avis.auteur?.nom || 'Inconnu' }} {{ avis.auteur?.prenom || '' }}</td>
              <td>{{ avis.auteur?.email || '-' }}</td>
              <td>
                <div>
                  {{ avis.talent?.nom || 'Inconnu' }} {{ avis.talent?.prenom || '' }}
                  <span v-if="avis.talent?.profilSignale" class="badge bg-warning text-dark ms-2" style="font-size: 10px;">
                    Signalé
                  </span>
                </div>
                <small class="text-muted" v-if="avis.talent && avis.talent.nombreAvisDefavorables > 0">
                  {{ avis.talent.nombreAvisDefavorables }} avis défavorable(s)
                </small>
              </td>
              <td>{{ avis.commentaire || '-' }}</td>
              <td>
                <span :class="avis.type === 'favorable' ? 'bg-success' : 'bg-danger'" class="badge">
                  {{ avis.type === 'favorable' ? 'Favorable' : 'Défavorable' }}
                </span>
              </td>
              <td>{{ avis.dateCommentaire ? format_date(avis.dateCommentaire) : '-' }}</td>
              <td>
                <div class="dropdown">
                  <span class="badge text-white bg-primary fs-15 dropdown-toggle" data-bs-toggle="dropdown">
                    Actions
                  </span>
                  <ul class="dropdown-menu">
                    <!-- Désactiver (seulement si signalé et actif) -->
                    <li v-if="avis.talent?.statut && avis.talent?.profilSignale">
                      <a class="dropdown-item text-danger" href="javascript:void(0);" @click="desactiverTalent(avis.talent.id)">
                        <i class="flaticon-locked me-2"></i> Désactiver le talent
                      </a>
                    </li>
                    <li v-else-if="avis.talent?.statut && !avis.talent?.profilSignale">
                      <span class="dropdown-item text-muted" style="cursor: not-allowed;">
                        <i class="flaticon-locked me-2"></i> Désactiver (non signalé)
                      </span>
                    </li>

                    <!-- Réactiver (seulement si inactif) -->
                    <li v-if="avis.talent && !avis.talent.statut">
                      <a class="dropdown-item text-success" href="javascript:void(0);" @click="reactiverTalent(avis.talent.id)">
                        <i class="flaticon-check-mark me-2"></i> Réactiver le talent
                      </a>
                    </li>

                    <li><hr class="dropdown-divider"></li>

                    <!-- Supprimer l'avis -->
                    <li>
                      <a class="dropdown-item text-danger" href="javascript:void(0);" @click="supprimerAvis(avis.id)">
                        <i class="flaticon-delete me-2"></i> Supprimer l'avis
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="aviss.length === 0">
              <td colspan="7" class="text-center py-3 text-body-tertiary">Aucun avis trouvé</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import ApiService from "../../services/ApiService";
import { format_date, suppression, error, success } from "../../utils/utils";
import PaginationComponent from "../../components/Utilities/Pagination.vue";
import Swal from "sweetalert2";

interface AvisTalent {
  id: number;
  commentaire: string;
  type: "favorable" | "defavorable";
  dateCommentaire: string | null;
  talent: {
    id: number;
    nom: string;
    prenom: string;
    profilSignale: boolean;
    nombreAvisDefavorables: number;
    statut: boolean;
  } | null;
  auteur: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
  } | null;
}

export default defineComponent({
  name: "ListeAvisTalents",
  components: { PaginationComponent },
  setup() {
    const aviss = ref<AvisTalent[]>([]);
    const searchTerm = ref("");
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(10);
    const totalElements = ref(0);

    // Charger les avis
    const getAllAvisUser = async () => {
      try {
        const response = await ApiService.get(`avis/users`);
        const resData = response.data;

        if (!resData || !Array.isArray(resData.data)) {
          aviss.value = [];
          totalPages.value = 0;
          totalElements.value = 0;
          return;
        }

        aviss.value = resData.data.map((avis: any) => ({
          id: avis.id,
          type: avis.type,
          commentaire: avis.commentaire || "-",
          dateCommentaire: avis.dateCommentaire || null,
          talent: avis.talent
            ? {
                id: avis.talent.id,
                nom: avis.talent.nom || "Inconnu",
                prenom: avis.talent.prenom || "",
                profilSignale: avis.talent.profilSignale || false,
                nombreAvisDefavorables: avis.talent.nombreAvisDefavorables || 0,
                statut: avis.talent.statut ?? true
              }
            : null,
          auteur: avis.auteur
            ? {
                id: avis.auteur.id,
                nom: avis.auteur.nom || "Inconnu",
                prenom: avis.auteur.prenom || "",
                email: avis.auteur.email || "-"
              }
            : null,
        }));

        totalPages.value = resData.totalPages || 1;
        totalElements.value = resData.totalElements || aviss.value.length;
        limit.value = resData.limit || limit.value;
      } catch (err: any) {
        error(err?.response?.data?.message || "Erreur lors de la récupération des avis");
        aviss.value = [];
      }
    };

    // Désactiver un talent
    const desactiverTalent = async (talentId: number) => {
      const result = await Swal.fire({
        title: 'Désactiver ce talent ?',
        text: 'Le talent ne sera plus visible dans les recherches',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, désactiver',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#dc3545'
      });

      if (result.isConfirmed) {
        try {
          await ApiService.post(`/talents/${talentId}/desactiver`, {});
          success("Talent désactivé avec succès");

          // Mise à jour locale
          const avis = aviss.value.find(a => a.talent?.id === talentId);
          if (avis?.talent) avis.talent.statut = false;

        } catch (err: any) {
          error(err?.response?.data?.message || "Erreur lors de la désactivation");
        }
      }
    };

    // Réactiver un talent
    const reactiverTalent = async (talentId: number) => {
      const result = await Swal.fire({
        title: 'Réactiver ce talent ?',
        text: 'Le talent redeviendra visible et actif',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, réactiver',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#28a745'
      });

      if (result.isConfirmed) {
        try {
          await ApiService.post(`/talents/${talentId}/reactiver`, {});
          success("Talent réactivé avec succès");

          const avis = aviss.value.find(a => a.talent?.id === talentId);
          if (avis?.talent) {
            avis.talent.statut = true;
            avis.talent.profilSignale = false;
            avis.talent.nombreAvisDefavorables = 0;
          }

        } catch (err: any) {
          error(err?.response?.data?.message || "Erreur lors de la réactivation");
        }
      }
    };

    // Supprimer un avis
    const supprimerAvis = async (avisId: number) => {
  await suppression(avisId, aviss, 'aviss', "l'avis");
  // Recharge automatique après suppression
  await getAllAvisUser();
};

    // Recherche
    const rechercher = () => {
      page.value = 1;
      getAllAvisUser();
    };

    // Pagination (simulée ici car API ne supporte pas)
    const handlePaginate = ({ page_: pageNumber, limit_: limitNumber }: { page_: number; limit_: number }) => {
      page.value = pageNumber;
      limit.value = limitNumber;
      getAllAvisUser();
    };

    // Charger au montage
    onMounted(() => {
      getAllAvisUser();
    });

    return {
      aviss,
      searchTerm,
      page,
      totalPages,
      totalElements,
      limit,
      handlePaginate,
      rechercher,
      format_date,
      desactiverTalent,
      reactiverTalent,
      supprimerAvis, // <-- Ajouté ici
    };
  },
});
</script>